import { Button, Tooltip } from '@mui/material';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import {
    useCreateContactField,
    useModifyContactField,
} from '../../../../sic-lib/api/hooks/useContact';
import { Contact, buildUpdateContact } from '../../../../sic-lib/types/Contact';
import { ContactFieldValue, buildCreateContactField } from '../../../../sic-lib/types/ContactField';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { createToast } from '../../../../store/notify';
import { createErrorToast } from '../../../../utils/notifyHelpers';
import MenuSetContactNameRaw from './MenuSetContactNameRaw/MenuSetContactNameRaw';

const useStyles = makeStyles()((theme: Theme) => ({
    btn: {
        cursor: 'pointer',
        color: theme.palette.btnDefault.main,
        '&:hover': {
            backgroundColor: theme.palette.btnDefault.light,
        },
    },
    icon: {
        marginLeft: theme.spacing(2),
    },
}));

interface Props {
    contact: Contact;
}

export default function MenuSetContactName({ contact }: Props) {
    const dispatch = useAppDispatch();
    const { classes } = useStyles();
    const queryClient = useQueryClient();
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const { mutate: modifyContactField } = useModifyContactField(contact.contactId);
    const { mutate: createContactField } = useCreateContactField(contact.contactId);

    const inboxFilter = useAppSelector(state => state.inbox.filters);

    const [compState, setCompState] = React.useState<{
        firstName: string;
        lastName: string;
        salutation: ContactFieldValue;
    }>({
        firstName: '',
        lastName: '',
        salutation: '',
    });

    React.useEffect(() => {
        setCompState({
            firstName: contact.firstName || '',
            lastName: contact.lastName || '',
            salutation: contact.values.find(itm => itm.fieldId === 15)?.value || '',
        });

        return () => {
            setAnchorEl(null);
        };
    }, [contact]);

    const menuId = `menu_contact_name_${contact.contactId}`;

    const handleSubmit = async (
        firstName: string,
        lastName: string,
        salutation: ContactFieldValue,
    ) => {
        console.log('MenuSetContactName: ', firstName, lastName, salutation);
        if (!firstName?.trim()) {
            /* Notify user of first name can not be empty */
            const toastData = createErrorToast('First name can not be empty');
            dispatch(createToast({ toastData }));
        } else {
            const requestFName = buildUpdateContact({
                leadId: contact.contactId,
                fieldType: 0,
                fieldId: 13,
                value: firstName,
            });

            const requestLName = buildUpdateContact({
                leadId: contact.contactId,
                fieldType: 0,
                fieldId: 14,
                value: lastName,
            });

            //update contact name
            modifyContactField(requestFName);
            modifyContactField(requestLName);
            salutation
                ? modifyContactField(
                      buildUpdateContact({
                          leadId: contact.contactId,
                          fieldType: 0,
                          fieldId: 15,
                          value: salutation,
                      }),
                  )
                : createContactField(
                      buildCreateContactField({
                          fieldName: 'value',
                          leadId: contact.contactId,
                          fieldType: 0,
                          fieldId: 15,
                          value: salutation,
                          isVerified: false,
                      }),
                  );

            //dispatch action to inbox reducer. the reason why dispatching 2 action is that the new/recent component tree does not use contactCache reducer.
            //for that reason It needs also to be updated on the inbox reducer
            queryClient.invalidateQueries({ queryKey: ['newAndRecent', inboxFilter.filter] });
        }

        handleClose();
    };

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <React.Fragment>
            {Boolean(anchorEl) ? (
                <MenuSetContactNameRaw
                    firstName={compState.firstName}
                    lastName={compState.lastName}
                    salutation={compState.salutation}
                    menuId={menuId}
                    onCloseHandler={handleClose}
                    onSubmitHandler={handleSubmit}
                />
            ) : (
                <Tooltip title="Edit Contact Name" placement="top" disableInteractive>
                    <Button
                        className={classes.btn}
                        aria-controls={menuId}
                        aria-expanded={Boolean(anchorEl)}
                        onClick={handleOpen}
                    >
                        {compState.firstName === '' ? (
                            <Typography component="h2" variant="h6" noWrap>
                                Contact Name
                            </Typography>
                        ) : (
                            <Typography component="h2" variant="h6">
                                {compState.firstName}{' '}
                                {compState.salutation !== '' && `"${compState.salutation}"`}{' '}
                                {compState.lastName}
                            </Typography>
                        )}
                    </Button>
                </Tooltip>
            )}
        </React.Fragment>
    );
}
